import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchPacSanaSummary,
  fetchPacSanaDayActivity,
  triggerPacSanaSync,
} from "../utils/api";

// The client's key is the prefix, so a sync invalidates every range fetched for
// that client, not only the one on screen.
const summaryKey = (clientId) => ["pacsana-summary", clientId || null];
const rangedKey = (clientId, range, events) => [
  ...summaryKey(clientId),
  range?.from || null,
  range?.to || null,
  events?.page || null,
  events?.pageSize || null,
];

// While a pull is running the panel is stale by definition, so it re-checks on
// this cadence until the backend reports the run finished.
const SYNC_POLL_MS = 6 * 1000;

/**
 * PacSana bracelet data for one client's Snapshot "PacSana" section.
 *
 * Returns { linked, user, device, activityStatus, readings, trendDays, sync } —
 * or { linked: false } when no PacSana user is linked to this client.
 *
 * Polls itself while `sync.inProgress` is true, so a sync started from the panel
 * lands on screen without the user reloading.
 *
 * `range` is an optional { from, to } pair of yyyy-MM-dd dates, and `events` an
 * optional { page, pageSize } for the event list PacSana serves on the same
 * call. Each combination is cached separately, so stepping back to a range or a
 * page already looked at is instant.
 */
export function usePacSanaSummary(
  clientId,
  { enabled = true, range = null, events = null } = {},
) {
  return useQuery({
    queryKey: rangedKey(clientId, range, events),
    queryFn: () => fetchPacSanaSummary(clientId, range, events),
    enabled: !!clientId && enabled,
    staleTime: 5 * 60 * 1000,
    // Changing the range swaps cache entry, which would otherwise blank the
    // panel — and the range filter with it — while the new one loads.
    placeholderData: (previous) => previous,
    refetchInterval: (query) =>
      query.state.data?.sync?.inProgress ? SYNC_POLL_MS : false,
  });
}

/**
 * One day of the 24-hour activity clock, fetched as the day picker moves.
 *
 * PacSana serves this a day at a time, so the panel asks for the day on screen
 * rather than the summary carrying a month of them. Each day is cached under its
 * own key, so stepping back to a day already looked at is instant, and the
 * backend stores what it fetches — the first look at a day is the slow one.
 */
export function usePacSanaDayActivity(clientId, date, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["pacsana-day-activity", clientId || null, date || null],
    queryFn: () => fetchPacSanaDayActivity(clientId, date),
    enabled: !!clientId && !!date && enabled,
    // A past day cannot change once PacSana has reported it; today still can,
    // but not fast enough to be worth refetching on every focus.
    staleTime: 10 * 60 * 1000,
    placeholderData: (previous) => previous,
  });
}

/**
 * Queues a PacSana refresh for the client's agency.
 *
 * The request answers as soon as the pull is queued, not when it finishes — the
 * summary query above takes over and polls for the result. Rejections carry the
 * backend's `{ status, message, cooldownSeconds }` on `error.response.data`
 * (429 = cooldown, 409 = paused / not connected, 404 = not linked).
 */
export function usePacSanaSync(clientId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => triggerPacSanaSync(clientId),
    onSuccess: (result) => {
      // Seed the sync state from the response so polling starts immediately
      // instead of waiting for a refetch to discover the run. Every range
      // cached for this client is seeded — the pull is roster-wide, so they are
      // all now waiting on the same run.
      queryClient.setQueriesData({ queryKey: summaryKey(clientId) }, (previous) =>
        previous
          ? { ...previous, sync: { ...previous.sync, ...result, canSync: false } }
          : previous,
      );
      queryClient.invalidateQueries({ queryKey: summaryKey(clientId) });
    },
  });
}

export default usePacSanaSummary;
